import './EditPagePresetModal.scss';
import cn from 'classnames';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd, MdClose, MdEdit } from 'react-icons/md';
import {
  getDefaultSparkChartMetrics,
  MASTER_COLUMN_MAP
} from '@blisspointmedia/bpm-types/dist/Columns';
import { Metric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { MASTER_METRIC_MAP } from '@blisspointmedia/bpm-types/dist/Metrics';
import { MetricsPagePreset } from '@blisspointmedia/bpm-types/dist/MetricsPage';
import { PresetIDGroups as TablePresetIDGroups } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import {
  CustomPresetState,
  INIT_CUSTOM_PRESET_STATE,
  RangeErrorPreset,
  ValidCustomPresetState
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { LINEAR, refactorEnabled } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { AdminLockToggle } from '@Components/AdminLockToggle/AdminLockToggle';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import { DPPresets } from '@Components/DatePicker/DPPresets/DPPresets';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { SourceType } from '@src/Pages/CrossChannel/Components/SourcePicker';
import { METRIC_OPTIONS } from '@src/Pages/CrossChannel/cross-channel-constants';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import { DateRange, snapToMonday } from '@src/utils/date-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useExperimentalAnalyticsCols } from '@src/utils/hooks/useExperimentalAnalyticsCols';
import { getLagPickerOptions, STANDARD_GROUP_NAME } from '@src/utils/performance-utils';
import { LagOption } from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import {
  getInvalidAnalyticsDetails,
  getInvalidGranularityDetailsFromComparingPresets,
  getInvalidGranularityDetailsFromPresetAndCols,
  InvalidGranularityDetails,
  makeInvalidDimMsg
} from '@src/utils/columnMetricSystem/configUtils';
import {
  DeliveryMetricOption,
  DEFAULT_DELIVERY_METRICS_OPTIONS
} from '../Delivery/SingleChannelDeliveryChart';

const SINGLE_CHANNEL_MENU_TABS = [
  'Dates',
  'KPI, etc.',
  'Filters',
  'Overview',
  'Delivery',
  'Metrics Table',
  'Preset Group'
];

interface GlobalOptionsPickers {
  audience?: string[] | undefined;
  kpi?: { options: { label: string; value: string }[] };
  lag?: boolean;
  source?:
    | {
        label: string;
        value: string;
      }[]
    | undefined;
  impsBetween?: boolean | undefined;
}

interface EditPagePresetModalProps {
  dateIncrementOptions?: ('daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')[];
  defaultLagWindow?: LagOption;
  deliveryDimensionMap?: Record<string, string>;
  deliveryMetricOptions?: DeliveryMetricOption[];
  filterPresets: FilterPreset[] | undefined;
  globalOptionsPickers: GlobalOptionsPickers;
  groups?: string[];
  mediatype?: string;
  menuTabs?: string[];
  onHide?: () => void;
  pagePresetChanges: MetricsPagePreset;
  saveAndApplyOnClick: (pagePresetChanges: MetricsPagePreset) => void;
  setPagePresetChanges: Dispatch<SetStateAction<MetricsPagePreset>>;
  sparkChartOptions?: { label: string; value: string }[];
  tablePresets: TablePresetIDGroups[];
  // the metrics table cols chosen in page preset changes; each filter preset is checked against them
  // Ex. Metrics table with dimension "retailer" means filter preset with dimension "keyword" are invalid
  tableColsFromTableChanges?: string[];
  // the filter dims chosen in page preset changes; each metrics table preset is checked against them
  // Ex. Filter with dimension "retailer" means metrics table preset with dimension "keyword" are invalid
  filterDimsFromFilterChanges?: string[];
  // same as the above but mapped for delivery which only reads pretty names in this component
  // Ex. Filter with dimension "retailer" means delivery dimension "keyword" is invalid
  incompatibleDeliveryDimsFromFilterChanges?: InvalidGranularityDetails;
  // which filter dimension are currently restricted because of the current delivery dimension selection in page preset changes
  // Ex. Delivery dimension "retailer" means metrics table preset including dimension "keyword" is invalid
  incompatibleFilterDimsFromDeliveryChanges?: InvalidGranularityDetails;
}

// A relative-date-info value is either a named preset string (e.g. 'Last 30 Days'), a resolved
// custom-range object, or absent — DPPresets only wants to know the active preset's name, so a
// resolved object is reported as the sentinel 'Custom' preset.
const resolveSelectedPreset = (
  relativeDateInfo: string | object | undefined
): string | 'Custom' | null => {
  if (typeof relativeDateInfo === 'string') return relativeDateInfo;
  if (relativeDateInfo) return 'Custom';
  return null;
};

// Use this when we haven't specified one in the map, single channel generally has 4 charts
const DEFAULT_SPARKCHARTS = ['spend', 'impressions', 'volume', 'roas'];
const EMPTY_COLS: string[] = [];
export const DEFAULT_SPARKCHARTS_MAP = {
  'cross-channel': [Metric.SPEND, Metric.IMPRESSIONS, Metric.CPM, Metric.VOLUME] as const,
  streaming: [Metric.SPEND, Metric.IMPRESSIONS, 'volume_direct', 'roasDirect'] as const,
  linear: ['spend', 'impressions', 'eCPM', 'volume'] as const
};

export const EditPagePresetModal = ({
  dateIncrementOptions,
  deliveryDimensionMap = {},
  deliveryMetricOptions = DEFAULT_DELIVERY_METRICS_OPTIONS,
  filterPresets = [],
  globalOptionsPickers = {},
  groups = [],
  mediatype,
  menuTabs = SINGLE_CHANNEL_MENU_TABS,
  onHide = () => {},
  pagePresetChanges,
  defaultLagWindow,
  saveAndApplyOnClick,
  setPagePresetChanges,
  sparkChartOptions = [],
  tablePresets = [],
  tableColsFromTableChanges = EMPTY_COLS,
  filterDimsFromFilterChanges = EMPTY_COLS,
  incompatibleDeliveryDimsFromFilterChanges = {},
  incompatibleFilterDimsFromDeliveryChanges = {}
}: EditPagePresetModalProps) => {
  const shouldEnableImpsBetweenToggle = useExperimentFlag('enableImpsBetweenToggle');
  const shouldEnableByResponseDate = useExperimentFlag('enableByResponseDate');
  const expAnalyticsCols = useExperimentalAnalyticsCols();
  const [selectedTab, setSelectedTab] = useState('Dates');
  const [newName, setNewName] = useState<string | undefined>();
  const [hasNewGroupName, setHasNewGroupName] = useState<boolean>();
  const [showGroupsDropdownMenu, setShowGroupsDropdownMenu] = useState<boolean | undefined>(
    undefined
  );
  const adminLock = Boolean(pagePresetChanges?.globalOptions?.admin_lock);
  const internalOnly = Boolean(
    pagePresetChanges?.globalOptions?.role && pagePresetChanges.globalOptions.role < 2
  );
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const [startCustomPreset, setStartCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...(typeof pagePresetChanges?.dateInfo?.relative_date_info === 'object'
      ? pagePresetChanges.dateInfo.relative_date_info.start
      : {})
  });
  const [endCustomPreset, setEndCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...(typeof pagePresetChanges?.dateInfo?.relative_date_info === 'object'
      ? pagePresetChanges.dateInfo.relative_date_info.end
      : {})
  });
  const [startCompCustomPreset, setStartCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...(typeof pagePresetChanges?.dateInfo?.comparison_relative_date_info === 'object'
      ? pagePresetChanges.dateInfo.comparison_relative_date_info.start
      : {})
  });
  const [endCompCustomPreset, setEndCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...(typeof pagePresetChanges?.dateInfo?.comparison_relative_date_info === 'object'
      ? pagePresetChanges.dateInfo.comparison_relative_date_info.end
      : {})
  });
  const [rangeErrorPreset, setRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [compRangeErrorPreset, setCompRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [dateInputs, setDateInputs] = useState<Partial<DateRange>>({
    start: pagePresetChanges.dateInfo.start,
    end: pagePresetChanges.dateInfo.end
  });
  const [comparisonDateInputs, setComparisonDateInputs] = useState<Partial<DateRange>>({
    start: pagePresetChanges.dateInfo.comparison_start,
    end: pagePresetChanges.dateInfo.comparison_end
  });

  const normalizeDate = useCallback(
    (date: string): string => (mediatype === LINEAR ? snapToMonday(date) : date),
    [mediatype]
  );

  const defaultSparkCharts = useMemo(() => {
    const channel = mediatype || '';
    if (refactorEnabled(channel)) {
      const defaults = getDefaultSparkChartMetrics(channel);
      if (defaults) {
        return defaults;
      }
    }
    return DEFAULT_SPARKCHARTS_MAP[`${mediatype}`] ?? DEFAULT_SPARKCHARTS;
  }, [mediatype]);

  const { kpi } = pagePresetChanges?.globalOptions?.kpi
    ? pagePresetChanges.globalOptions
    : { kpi: undefined };
  const { audience } = pagePresetChanges?.globalOptions?.audience
    ? pagePresetChanges.globalOptions
    : { audience: 'HH' };
  const { lag } = pagePresetChanges?.globalOptions?.lag
    ? pagePresetChanges.globalOptions
    : { lag: undefined };
  const { source } = pagePresetChanges?.globalOptions?.source
    ? pagePresetChanges.globalOptions
    : { source: 'platform' };
  const { shouldUseUnequivalizedImpressions } = pagePresetChanges?.globalOptions
    ?.shouldUseUnequivalizedImpressions
    ? pagePresetChanges.globalOptions
    : { shouldUseUnequivalizedImpressions: false };
  const impsBetween = pagePresetChanges?.globalOptions?.impsBetween ?? true;

  const dateIncrementsOptions = useMemo(() => {
    const options = [
      { label: 'Daily', value: 'daily' },
      { label: 'Weekly', value: 'weekly' },
      { label: 'Monthly', value: 'monthly' },
      { label: 'Quarterly', value: 'quarterly' },
      { label: 'Yearly', value: 'yearly' }
    ];
    if (dateIncrementOptions) {
      const newOptions = options.filter(option =>
        dateIncrementOptions.includes(
          option.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
        )
      );
      if (
        pagePresetChanges?.dateInfo?.increments &&
        !newOptions.map(option => option.value).includes(pagePresetChanges.dateInfo.increments)
      ) {
        setPagePresetChanges(current => ({
          ...current,
          dateInfo: {
            ...current.dateInfo,
            increments: newOptions[0].value as
              'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
          }
        }));
      }
      return newOptions;
    }
    return options;
  }, [dateIncrementOptions, pagePresetChanges, setPagePresetChanges]);

  const disableApply = useMemo(() => {
    if (adminLock && !isAdmin) {
      return true;
    }
    if (pagePresetChanges.dateInfo) {
      return (
        pagePresetChanges.dateInfo.relative_date_info === 'Custom' ||
        pagePresetChanges.dateInfo.comparison_relative_date_info === 'Custom'
      );
    }
    return false;
  }, [adminLock, isAdmin, pagePresetChanges]);

  useEffect(() => {
    setPagePresetChanges(p => {
      const { number: startNum } = startCustomPreset;
      const { number: endNum } = endCustomPreset;
      const shouldSet =
        p.dateInfo.dateType === 'relative' &&
        (p.dateInfo.relative_date_info === 'Custom' ||
          typeof p.dateInfo.relative_date_info === 'object') &&
        typeof startNum === 'number' &&
        typeof endNum === 'number';

      if (shouldSet) {
        return {
          ...p,
          dateInfo: {
            ...p.dateInfo,
            relative_date_info: {
              start: { ...(startCustomPreset as ValidCustomPresetState) },
              end: { ...(endCustomPreset as ValidCustomPresetState) }
            }
          }
        };
      }
      if (typeof p.dateInfo.relative_date_info === 'object') {
        return { ...p, dateInfo: { ...p.dateInfo, relative_date_info: 'Custom' } };
      }
      return p;
    });
  }, [startCustomPreset, endCustomPreset, setPagePresetChanges]);

  useEffect(() => {
    setPagePresetChanges(p => {
      const { number: startNum } = startCompCustomPreset;
      const { number: endNum } = endCompCustomPreset;

      const shouldSet =
        p.dateInfo.comparison_dateType === 'relative' &&
        (p.dateInfo.comparison_relative_date_info === 'Custom' ||
          typeof p.dateInfo.comparison_relative_date_info === 'object') &&
        typeof startNum === 'number' &&
        typeof endNum === 'number';

      if (shouldSet) {
        return {
          ...p,
          dateInfo: {
            ...p.dateInfo,
            comparison_relative_date_info: {
              start: { ...(startCompCustomPreset as ValidCustomPresetState) },
              end: { ...(endCompCustomPreset as ValidCustomPresetState) }
            }
          }
        };
      }
      if (typeof p.dateInfo.comparison_relative_date_info === 'object') {
        return { ...p, dateInfo: { ...p.dateInfo, comparison_relative_date_info: 'Custom' } };
      }
      return p;
    });
  }, [startCompCustomPreset, endCompCustomPreset, setPagePresetChanges]);

  useEffect(() => {
    if (dateInputs.start && dateInputs.end) {
      const start = normalizeDate(dateInputs.start);
      const end = normalizeDate(dateInputs.end);
      setPagePresetChanges(p =>
        p.dateInfo.start === start && p.dateInfo.end === end
          ? p
          : { ...p, dateInfo: { ...p.dateInfo, start, end } }
      );
    }
  }, [setPagePresetChanges, dateInputs, normalizeDate]);

  useEffect(() => {
    if (comparisonDateInputs.start && comparisonDateInputs.end) {
      const start = normalizeDate(comparisonDateInputs.start);
      const end = normalizeDate(comparisonDateInputs.end);
      setPagePresetChanges(p =>
        p.dateInfo.comparison_start === start && p.dateInfo.comparison_end === end
          ? p
          : { ...p, dateInfo: { ...p.dateInfo, comparison_start: start, comparison_end: end } }
      );
    }
  }, [setPagePresetChanges, comparisonDateInputs, normalizeDate]);

  useEffect(() => {
    if (
      pagePresetChanges?.globalOptions &&
      ((!pagePresetChanges.globalOptions.audience && audience) ||
        (!pagePresetChanges.globalOptions.kpi && kpi) ||
        (!pagePresetChanges.globalOptions.lag && lag) ||
        (!pagePresetChanges.globalOptions.source && source) ||
        (pagePresetChanges.globalOptions.shouldUseUnequivalizedImpressions == null &&
          shouldUseUnequivalizedImpressions != null) ||
        (pagePresetChanges.globalOptions.impsBetween == null && impsBetween != null))
    ) {
      setPagePresetChanges(current => ({
        ...current,
        globalOptions: {
          ...current.globalOptions,
          audience: audience as 'HH' | 'A18+' | 'M18+' | 'W18+' | 'P2+',
          kpi: kpi as string,
          lag,
          source,
          shouldUseUnequivalizedImpressions,
          impsBetween
        }
      }));
    }
  }, [
    audience,
    kpi,
    lag,
    pagePresetChanges,
    setPagePresetChanges,
    shouldUseUnequivalizedImpressions,
    source,
    impsBetween
  ]);

  useEffect(() => {
    if (
      pagePresetChanges?.globalOptions?.tableID != null &&
      tablePresets &&
      !tablePresets
        .map(tablePreset => tablePreset.id)
        .includes(pagePresetChanges.globalOptions.tableID)
    ) {
      setPagePresetChanges(current => ({
        ...current,
        globalOptions: { ...current.globalOptions, tableID: tablePresets[0].id }
      }));
    }
  }, [pagePresetChanges, setPagePresetChanges, tablePresets]);

  const sparkCharts = useMemo(() => {
    const savedSparkCharts = pagePresetChanges?.globalOptions
      ? (pagePresetChanges.globalOptions.sparkCharts ?? [])
      : defaultSparkCharts;
    const resolvedSparkCharts = [...savedSparkCharts];
    let resetCharts = false;
    for (let i = 0; i < defaultSparkCharts.length; i += 1) {
      if (!resolvedSparkCharts[i]) {
        resetCharts = true;
        resolvedSparkCharts[i] = defaultSparkCharts[i];
      }
    }
    if (resetCharts) {
      setPagePresetChanges(current => ({
        ...current,
        globalOptions: { ...current.globalOptions, sparkCharts: resolvedSparkCharts }
      }));
    }
    return resolvedSparkCharts;
  }, [defaultSparkCharts, pagePresetChanges, setPagePresetChanges]);

  const kpiPicker = globalOptionsPickers.kpi?.options && (
    <Dropdown
      label='KPI'
      onChange={option => {
        if (globalOptionsPickers.kpi) {
          setPagePresetChanges({
            ...pagePresetChanges,
            globalOptions: { ...pagePresetChanges.globalOptions, kpi: option }
          });
        }
      }}
      options={globalOptionsPickers.kpi.options}
      size={Size.MD}
      value={kpi}
    />
  );
  const lagPicker = globalOptionsPickers.lag && (
    <Dropdown
      label='Lag'
      onChange={option =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: {
            ...pagePresetChanges.globalOptions,
            lag: option
          }
        })
      }
      options={getLagPickerOptions(defaultLagWindow)}
      size={Size.MD}
      value={lag ?? defaultLagWindow ?? '7d'}
    />
  );
  const audiencePicker = globalOptionsPickers.audience && (
    <Dropdown
      label='Audience'
      onChange={option =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: {
            ...pagePresetChanges.globalOptions,
            audience: option as 'HH' | 'A18+' | 'M18+' | 'W18+' | 'P2+'
          }
        })
      }
      options={globalOptionsPickers.audience}
      size={Size.MD}
      value={audience ?? 'HH'}
    />
  );

  const shouldUseUnequivalizedImpressionsPicker = globalOptionsPickers.audience && (
    <ToggleSwitch
      label='Use Unequivalized Impressions?'
      checked={shouldUseUnequivalizedImpressions}
      onChange={() =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: {
            ...pagePresetChanges.globalOptions,
            shouldUseUnequivalizedImpressions: !shouldUseUnequivalizedImpressions
          }
        })
      }
    />
  );

  const impsBetweenPicker = globalOptionsPickers.impsBetween && shouldEnableImpsBetweenToggle && (
    <SegmentedControlButton
      options={['Imps Between', 'Response Between']}
      selectedOption={impsBetween ? 'Imps Between' : 'Response Between'}
      onChange={() =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: { ...pagePresetChanges.globalOptions, impsBetween: !impsBetween }
        })
      }
    />
  );

  const modalHeader = (
    <Modal.Header>
      <div className='title-container'>
        <Modal.Title>Page Preset:</Modal.Title>
        <div className='page-preset-name-container'>
          {newName !== undefined ? (
            <Form.Control
              className='new-name-input'
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              placeholder='New Preset Name'
              value={newName}
            />
          ) : (
            <div className='page-preset-name'>
              {pagePresetChanges.temporary ? 'Temporary Preset' : pagePresetChanges.name}
            </div>
          )}
          {pagePresetChanges?.name !== 'Default' &&
            (newName === undefined ? (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='edit-page-preset-name-icon'
                onClick={() => setNewName(pagePresetChanges.name)}
              >
                <MdEdit />
              </IconButton>
            ) : (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='edit-page-preset-name-icon'
                onClick={() => setNewName(undefined)}
              >
                <MdClose />
              </IconButton>
            ))}
        </div>
      </div>
      <AdminLockToggle
        checked={adminLock}
        onChange={() =>
          setPagePresetChanges({
            ...pagePresetChanges,
            globalOptions: { ...pagePresetChanges.globalOptions, admin_lock: !adminLock }
          })
        }
        className='internal-only-toggle'
        disabled={!isAdmin}
      />
      <ToggleSwitch
        checked={internalOnly}
        className='internal-only-toggle'
        design={Design.BRAND}
        label='Internal-only'
        onChange={() =>
          setPagePresetChanges({
            ...pagePresetChanges,
            globalOptions: { ...pagePresetChanges.globalOptions, role: internalOnly ? 10 : 1 }
          })
        }
      />
    </Modal.Header>
  );

  const tabSelector = (
    <div className='tab-selector'>
      {menuTabs
        .filter(
          tab => tab !== 'Preset Group' || pagePresetChanges.presetGroup !== STANDARD_GROUP_NAME
        )
        .map(tab => (
          <InteractiveDiv
            key={`editPresetModalTabSelector${tab}`}
            className={cn('tab', { active: selectedTab === tab })}
            onClick={() => setSelectedTab(tab)}
          >
            {tab}
          </InteractiveDiv>
        ))}
    </div>
  );

  const metricOptions = useMemo(() => {
    if (sparkChartOptions) {
      const options: { label: string; value: Metric }[] = [];
      for (const sparkChartOption of sparkChartOptions) {
        const metric = sparkChartOption.value as Metric;
        const matchingMetricOption = METRIC_OPTIONS.find(option => option.value === metric);
        if (refactorEnabled(mediatype || '')) {
          const metricOption = MASTER_COLUMN_MAP[metric] || MASTER_METRIC_MAP[metric] || null;
          if (metricOption) {
            options.push({ label: metricOption.prettyName, value: metric });
          }
        } else if (matchingMetricOption) {
          options.push(matchingMetricOption);
        } else {
          options.push(sparkChartOption as { label: string; value: Metric });
        }
      }
      return options;
    }
    return METRIC_OPTIONS;
  }, [sparkChartOptions, mediatype]);

  const datesContainer = (
    <div className='dates-container'>
      <div className='date-picker-container'>
        <div className='date-picker-label'>Primary Period</div>
        <div className='date-picker-container'>
          <div className='date-type-toggle-container'>
            <SegmentedControlButton
              options={['Relative', 'Exact']}
              selectedOption={
                pagePresetChanges.dateInfo.dateType === 'exact' ? 'Exact' : 'Relative'
              }
              onChange={() =>
                setPagePresetChanges(p => {
                  const { relative_date_info: _relativeDateInfo, ...rest } = p.dateInfo;
                  return {
                    ...p,
                    dateInfo: {
                      ...rest,
                      dateType: p.dateInfo.dateType === 'relative' ? 'exact' : 'relative'
                    }
                  };
                })
              }
              design={Design.NEUTRAL}
            />
          </div>
          {pagePresetChanges.dateInfo.dateType === 'exact' ? (
            <div className='date-field-container'>
              <DateInputField
                label='Start'
                value={dateInputs.start || ''}
                setValue={(value: string) =>
                  setDateInputs(d =>
                    !d.end || value > d.end ? { start: value } : { ...d, start: value }
                  )
                }
              />
              <DateInputField
                label='End'
                value={dateInputs.end || ''}
                setValue={(value: string) =>
                  setDateInputs(d =>
                    !d.start || value < d.start ? { end: value } : { ...d, end: value }
                  )
                }
              />
            </div>
          ) : (
            <div className='date-presets-container'>
              <DPPresets
                setSelectedPreset={preset => {
                  setPagePresetChanges(p => ({
                    ...p,
                    dateInfo: { ...p.dateInfo, relative_date_info: preset }
                  }));
                }}
                selectedPreset={resolveSelectedPreset(
                  pagePresetChanges.dateInfo.relative_date_info
                )}
                setDateInputs={setDateInputs}
                setRangeErrorPreset={setRangeErrorPreset}
                startCustomPreset={startCustomPreset}
                setStartCustomPreset={setStartCustomPreset}
                endCustomPreset={endCustomPreset}
                setEndCustomPreset={setEndCustomPreset}
                rangeErrorPreset={rangeErrorPreset}
              />
            </div>
          )}
        </div>
      </div>
      <div className='date-picker-container'>
        <div className='date-picker-label'>Comparison Period</div>
        <div className='date-picker-container'>
          <div className='date-type-toggle-container'>
            <SegmentedControlButton
              options={['Relative', 'Exact']}
              selectedOption={
                pagePresetChanges.dateInfo.comparison_dateType === 'exact' ? 'Exact' : 'Relative'
              }
              onChange={() =>
                setPagePresetChanges(p => {
                  const { comparison_relative_date_info: _comparisonRelativeDateInfo, ...rest } =
                    p.dateInfo;
                  return {
                    ...p,
                    dateInfo: {
                      ...rest,
                      comparison_dateType:
                        p.dateInfo.comparison_dateType === 'relative' ? 'exact' : 'relative'
                    }
                  };
                })
              }
              design={Design.NEUTRAL}
            />
          </div>
          {pagePresetChanges.dateInfo.comparison_dateType === 'exact' ? (
            <div className='date-field-container'>
              <DateInputField
                label='Comparison Start'
                value={comparisonDateInputs.start || ''}
                setValue={(value: string) =>
                  setComparisonDateInputs(d =>
                    !d.end || value > d.end ? { start: value } : { ...d, start: value }
                  )
                }
              />
              <DateInputField
                label='Comparison End'
                value={comparisonDateInputs.end || ''}
                setValue={(value: string) =>
                  setComparisonDateInputs(d =>
                    !d.start || value < d.start ? { end: value } : { ...d, end: value }
                  )
                }
              />
            </div>
          ) : (
            <div className='date-presets-container'>
              <DPPresets
                setSelectedPreset={preset => {
                  setPagePresetChanges(p => ({
                    ...p,
                    dateInfo: { ...p.dateInfo, comparison_relative_date_info: preset }
                  }));
                }}
                selectedPreset={resolveSelectedPreset(
                  pagePresetChanges.dateInfo.comparison_relative_date_info
                )}
                setRangeErrorPreset={setCompRangeErrorPreset}
                startCustomPreset={startCompCustomPreset}
                setStartCustomPreset={setStartCompCustomPreset}
                endCustomPreset={endCompCustomPreset}
                setEndCustomPreset={setEndCompCustomPreset}
                rangeErrorPreset={compRangeErrorPreset}
                setDateInputs={setComparisonDateInputs}
                comparison
              />
            </div>
          )}
        </div>
      </div>
      <div className='date-increments-container'>
        <div className='date-increments-label'>Date Increments</div>
        <div className='date-increments-dropdown-container'>
          <Dropdown
            size={Size.MD}
            className='date-increments-dropdown'
            value={pagePresetChanges?.dateInfo?.increments ?? 'daily'}
            options={dateIncrementsOptions}
            onChange={value =>
              setPagePresetChanges({
                ...pagePresetChanges,
                dateInfo: {
                  ...pagePresetChanges.dateInfo,
                  increments: value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
                }
              })
            }
          />
        </div>
      </div>
    </div>
  );

  const filterPresetOptionsMetadata = useMemo(
    () =>
      filterPresets.map(preset => {
        const [hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames] = getInvalidAnalyticsDetails(
          preset,
          true,
          expAnalyticsCols,
          'filter'
        );

        return {
          ...preset,
          name: preset.name,
          id: preset.id,
          hasInvalidAnalyicsDims,
          invalidAnalyticDimPrettyNames
        };
      }),
    [filterPresets, expAnalyticsCols]
  );

  const filtersContainer = useMemo(() => {
    const filterPresetOptions = filterPresetOptionsMetadata.map(preset => {
      const { name, id, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames } = preset;
      const analyticsIsSource =
        Boolean(source) && source !== SourceType.PLATFORM && source !== SourceType.GOOGLE_ADS_HUB;

      const [invalidPrettyDimsFromPreset, activeInvalidDimsFromOtherPreset] =
        getInvalidGranularityDetailsFromPresetAndCols(
          preset,
          tableColsFromTableChanges,
          mediatype || '',
          'filter'
        );

      const [invalidPrettyDimsFromDelivery, activeInvalidDimsFromDelivery] =
        getInvalidGranularityDetailsFromComparingPresets(
          preset,
          incompatibleFilterDimsFromDeliveryChanges,
          'filter'
        );

      const isDisabled = Boolean(
        (analyticsIsSource && hasInvalidAnalyicsDims) ||
        (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset) ||
        (invalidPrettyDimsFromDelivery && activeInvalidDimsFromDelivery)
      );

      const tooltipMessage = makeInvalidDimMsg({
        prettyDims:
          analyticsIsSource && hasInvalidAnalyicsDims
            ? invalidAnalyticDimPrettyNames
            : (invalidPrettyDimsFromPreset ?? invalidPrettyDimsFromDelivery),
        isInvalidAnalyticsDim: analyticsIsSource && hasInvalidAnalyicsDims,
        conflictingTableDims: activeInvalidDimsFromOtherPreset,
        conflictingDeliveryDims: activeInvalidDimsFromDelivery,
        type: 'filter'
      });

      return {
        label: name,
        value: `${id}`,
        disabled: isDisabled,
        simpleTooltip: isDisabled ? tooltipMessage : undefined,
        simpleTooltipVerticalShift: '-55px'
      };
    });

    return (
      <div className='filters-container'>
        <div className='filter-preset-container'>
          <div className='filter-preset-label'>Filter Preset</div>
          <div className='filter-preset-dropdown-container'>
            <Dropdown
              size={Size.MD}
              className='filter-preset-dropdown'
              value={
                pagePresetChanges?.filterInfo.filterID
                  ? `${pagePresetChanges.filterInfo.filterID}`
                  : 'None'
              }
              options={[{ label: 'None', value: 'None' }, ...filterPresetOptions]}
              onChange={value => {
                if (value === 'None') {
                  setPagePresetChanges({ ...pagePresetChanges, filterInfo: {} });
                } else {
                  setPagePresetChanges({
                    ...pagePresetChanges,
                    filterInfo: { filterID: parseInt(value, 10) }
                  });
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }, [
    pagePresetChanges,
    setPagePresetChanges,
    source,
    filterPresetOptionsMetadata,
    tableColsFromTableChanges,
    mediatype,
    incompatibleFilterDimsFromDeliveryChanges
  ]);
  const groupsContainer = (
    <div className='groups-container'>
      <div className='group-container'>
        <div className='group-label'>Preset Group</div>
        <div className='group-selection-container'>
          <Dropdown
            value={hasNewGroupName ? 'New Group' : (pagePresetChanges?.presetGroup ?? '')}
            options={[
              ...(hasNewGroupName ? [{ label: 'New Group', value: 'New Group' }] : []),
              ...groups
            ]}
            size={Size.MD}
            show={showGroupsDropdownMenu}
            onChange={value => {
              setPagePresetChanges(current => ({ ...current, presetGroup: value }));
              setHasNewGroupName(false);
            }}
            onToggle={() => setShowGroupsDropdownMenu(!showGroupsDropdownMenu)}
            footer={
              !hasNewGroupName ? (
                <>
                  <MdAdd />
                  <div className='label'>Create New Group</div>
                </>
              ) : undefined
            }
            footerOnClick={() => {
              setHasNewGroupName(true);
              setShowGroupsDropdownMenu(false);
            }}
          />
          {hasNewGroupName && (
            <div className='group-form-container'>
              <Form.Control
                className='new-name-input'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const newGroupName = e.target.value;
                  if (newGroupName != null) {
                    setPagePresetChanges(current => ({ ...current, presetGroup: newGroupName }));
                  }
                }}
                placeholder='None'
                value={pagePresetChanges?.presetGroup ?? ''}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Both the single channel and cross channel overview modals use the same compare metrics subcontainer
  const compareMetricsSubContainer = (
    <div className='cm-container'>
      <div className='cm-label'>Compare Metrics</div>
      <div className='cm-dropdowns-container'>
        <div className='toggle'>
          <ToggleSwitch
            design={Design.NEUTRAL}
            label='Compare Period'
            checked={pagePresetChanges?.globalOptions?.compareMetricsIncludeOtherDates ?? false}
            onChange={() =>
              setPagePresetChanges({
                ...pagePresetChanges,
                globalOptions: {
                  ...pagePresetChanges.globalOptions,
                  compareMetricsIncludeOtherDates: !(
                    pagePresetChanges?.globalOptions?.compareMetricsIncludeOtherDates ?? false
                  )
                }
              })
            }
          />
        </div>
        {Array.from({ length: 4 }, (_v, index) => (
          <div className='cm-dropdown-container' key={`cmDropdownItemMetric${index}`}>
            <div className='label'>{`Metric ${index + 1}`}</div>
            <Dropdown
              size={Size.MD}
              value={pagePresetChanges?.globalOptions?.compareMetrics?.[index]}
              options={[...metricOptions, { label: 'None', value: undefined }] as DropdownOption[]}
              onChange={value => {
                const newCompareMetrics = pagePresetChanges?.globalOptions?.compareMetrics ?? [
                  undefined,
                  undefined,
                  undefined,
                  undefined
                ];
                newCompareMetrics[index] = value as Metric;
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    compareMetrics: newCompareMetrics
                  }
                });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const sparkChartsSection = (containerClassName: string, tabKey: string) => (
    <div className={containerClassName}>
      <div className='spark-charts-container'>
        <div className='spark-charts-label'>Spark Charts</div>
        <div className='spark-chart-selectors-container'>
          {sparkCharts.map((chart, index) => (
            <div className='spark-chart-selector' key={`sparkChartSelector${tabKey}${chart}`}>
              <div className='label'>{`Chart ${index + 1}`}</div>
              {!(
                chart.toLowerCase() === 'roas' ||
                (chart.length === 3 && chart.toLowerCase().startsWith('cp'))
              ) && (
                <SegmentedControlButton
                  design={Design.NEUTRAL}
                  size={Size.MD}
                  options={['Total', 'Avg']}
                  selectedOption={
                    pagePresetChanges.globalOptions.sparkChartTypes?.[index]
                      ? pagePresetChanges.globalOptions.sparkChartTypes[index]
                      : 'Total'
                  }
                  onChange={value => {
                    const newSparkChartTypes = pagePresetChanges.globalOptions.sparkChartTypes
                      ? [...pagePresetChanges.globalOptions.sparkChartTypes]
                      : [false, false, false, false];
                    newSparkChartTypes[index] = value as Metric;
                    setPagePresetChanges(changes => ({
                      ...changes,
                      globalOptions: {
                        ...changes.globalOptions,
                        sparkChartTypes: newSparkChartTypes
                      }
                    }));
                  }}
                />
              )}
              <Dropdown
                size={Size.MD}
                options={metricOptions}
                value={chart}
                onChange={value => {
                  const newSparkCharts = [...sparkCharts];
                  newSparkCharts[index] = value;
                  setPagePresetChanges(changes => ({
                    ...changes,
                    globalOptions: { ...changes.globalOptions, sparkCharts: newSparkCharts }
                  }));
                }}
              />
            </div>
          ))}
        </div>
      </div>
      {compareMetricsSubContainer}
    </div>
  );

  const overviewContainer = sparkChartsSection('overview-container', 'overview');

  const tablePresetOptionsMetadata = useMemo(
    () =>
      tablePresets.map(preset => {
        const [hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames] = getInvalidAnalyticsDetails(
          preset,
          true,
          expAnalyticsCols,
          'table'
        );
        return {
          ...preset,
          name: preset.name,
          id: preset.id,
          hasInvalidAnalyicsDims,
          invalidAnalyticDimPrettyNames
        };
      }),
    [tablePresets, expAnalyticsCols]
  );

  const metricsTableContainer = useMemo(() => {
    const tableOptions = tablePresetOptionsMetadata.map(preset => {
      const { name, id, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames } = preset;
      const analyticsIsSource =
        Boolean(source) && source !== SourceType.PLATFORM && source !== SourceType.GOOGLE_ADS_HUB;

      const [invalidPrettyDimsFromPreset, activeInvalidDimsFromOtherPreset] =
        getInvalidGranularityDetailsFromPresetAndCols(
          preset,
          filterDimsFromFilterChanges,
          mediatype || '',
          'table'
        );

      const isDisabled = Boolean(
        (analyticsIsSource && hasInvalidAnalyicsDims) ||
        (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset)
      );

      const tooltipMessage = makeInvalidDimMsg({
        prettyDims:
          hasInvalidAnalyicsDims && analyticsIsSource
            ? invalidAnalyticDimPrettyNames
            : invalidPrettyDimsFromPreset,
        isInvalidAnalyticsDim: hasInvalidAnalyicsDims && analyticsIsSource,
        conflictingFilterDims: activeInvalidDimsFromOtherPreset,
        type: 'table'
      });

      return {
        label: name,
        value: `${id}`,
        disabled: isDisabled,
        simpleTooltip: isDisabled ? tooltipMessage : undefined,
        simpleTooltipVerticalShift: '-70px'
      };
    });

    return (
      <div className='metrics-table-container'>
        <div className='table-preset-container'>
          <div className='table-preset-label'>Table Preset</div>
          <div className='table-preset-dropdown-container'>
            <Dropdown
              size={Size.MD}
              className='table-preset-dropdown'
              value={`${pagePresetChanges.globalOptions.tableID}`}
              options={tableOptions}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    tableID: parseInt(value, 10)
                  }
                })
              }
            />
          </div>
        </div>
      </div>
    );
  }, [
    pagePresetChanges,
    setPagePresetChanges,
    source,
    tablePresetOptionsMetadata,
    filterDimsFromFilterChanges,
    mediatype
  ]);
  const sourcePicker = useMemo(() => {
    const isRefactoredSCP = refactorEnabled(mediatype || '');
    if (!globalOptionsPickers.source) return null;

    let disableByPresets = false;
    let tooltiptextStr;
    let verticalShift = 55;
    let plural = false;

    if (isRefactoredSCP) {
      const activeTablePresetFromMetaData = tablePresetOptionsMetadata.find(
        tablePreset => tablePreset.id === pagePresetChanges.globalOptions.tableID
      );
      const activeFilterPresetFromMetaData = filterPresetOptionsMetadata.find(
        filterPreset => filterPreset.id === pagePresetChanges.filterInfo.filterID
      );
      if (activeTablePresetFromMetaData) {
        const {
          hasInvalidAnalyicsDims: hasInvalidAnalyicsDimsTable,
          invalidAnalyticDimPrettyNames: invalidAnalyticDimPrettyNamesTable
        } = activeTablePresetFromMetaData;
        if (hasInvalidAnalyicsDimsTable && invalidAnalyticDimPrettyNamesTable) {
          disableByPresets = true;
          const dimNames = invalidAnalyticDimPrettyNamesTable;
          const dimsPlural = dimNames.length > 1;
          verticalShift = 70;
          if (dimsPlural) {
            plural = true;
            tooltiptextStr = `Metrics table dimensions ${dimNames.join(', and ')}`;
          } else {
            // this will only have one element max but safer if array is empty
            tooltiptextStr = `Metrics table dimension ${dimNames.join(', and ')}`;
          }
        }
      }
      if (activeFilterPresetFromMetaData) {
        const {
          hasInvalidAnalyicsDims: hasInvalidAnalyicsDimsFilter,
          invalidAnalyticDimPrettyNames: invalidAnalyticDimPrettyNamesFilter
        } = activeFilterPresetFromMetaData;
        if (hasInvalidAnalyicsDimsFilter && invalidAnalyticDimPrettyNamesFilter) {
          disableByPresets = true;
          if (tooltiptextStr) {
            tooltiptextStr = `${tooltiptextStr} and `;
            verticalShift = 90;
            plural = true;
          }
          const dimNames = invalidAnalyticDimPrettyNamesFilter;
          const filterMessage = `Filter with ${dimNames.join(', and ')}`;
          tooltiptextStr = `${tooltiptextStr || ''} ${filterMessage}`;
        }
      }
      if (disableByPresets && tooltiptextStr) {
        const msgEnding = plural ? 'are incompatible with SoT' : 'is incompatible with SoT';
        tooltiptextStr = `${tooltiptextStr} ${msgEnding}`;
      }
    }

    const sourceOptionsParsed = globalOptionsPickers.source.map(sourceOption => {
      const isPlatform = sourceOption.value === 'platform';
      return {
        label: sourceOption.label,
        value: sourceOption.value,
        disabled: !isPlatform && disableByPresets,
        simpleTooltip: tooltiptextStr,
        simpleTooltipVerticalShift: `-${verticalShift}px`
      };
    });

    return (
      <Dropdown
        label='Source'
        onChange={option =>
          setPagePresetChanges({
            ...pagePresetChanges,
            globalOptions: { ...pagePresetChanges.globalOptions, source: option }
          })
        }
        options={sourceOptionsParsed}
        size={Size.MD}
        value={source ?? 'platform'}
      />
    );
  }, [
    globalOptionsPickers.source,
    mediatype,
    pagePresetChanges,
    setPagePresetChanges,
    source,
    filterPresetOptionsMetadata,
    tablePresetOptionsMetadata
  ]);

  const deliveryContainer = (
    <div className='delivery-container'>
      <div className='delivery-chart-container'>
        <div className='delivery-label'>Delivery Options</div>
        <div className='delivery-options-container'>
          <Dropdown
            size={Size.MD}
            options={deliveryMetricOptions.map(option => ({
              label: option.label,
              value: option.label
            }))}
            value={pagePresetChanges.globalOptions.deliveryMetric ?? deliveryMetricOptions[0].label}
            onChange={value =>
              setPagePresetChanges({
                ...pagePresetChanges,
                globalOptions: { ...pagePresetChanges.globalOptions, deliveryMetric: value }
              })
            }
          />
          <div className='delivery-option'>
            <div className='label'>Delivery by</div>
            <Dropdown
              size={Size.MD}
              options={Object.keys(deliveryDimensionMap).map(key => {
                const conflictingDims = incompatibleDeliveryDimsFromFilterChanges[key];
                return {
                  label: key,
                  value: key,
                  disabled: Boolean(conflictingDims?.length),
                  simpleTooltip: conflictingDims?.length
                    ? makeInvalidDimMsg({
                        prettyDims: [key],
                        isInvalidAnalyticsDim: false,
                        conflictingFilterDims: conflictingDims
                      })
                    : undefined,
                  simpleTooltipVerticalShift: '-55px'
                };
              })}
              value={
                pagePresetChanges.globalOptions.deliveryDimension ??
                Object.keys(deliveryDimensionMap)[0]
              }
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, deliveryDimension: value }
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const globalOptionsContainer = (
    <div className='global-options-container'>
      {kpiPicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>KPI</div>
          <div className='global-options-dropdown'>{kpiPicker}</div>
        </div>
      )}
      {lagPicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Lag Window</div>
          <div className='global-options-dropdown'>{lagPicker}</div>
        </div>
      )}
      {audiencePicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Audience</div>
          <div className='global-options-dropdown'>{audiencePicker}</div>
        </div>
      )}
      {sourcePicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Source</div>
          <div className='global-options-dropdown'>{sourcePicker}</div>
        </div>
      )}
      {shouldUseUnequivalizedImpressionsPicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Use Unequivalized Impressions?</div>
          <div className='global-options-dropdown'>{shouldUseUnequivalizedImpressionsPicker}</div>
        </div>
      )}
      {shouldEnableByResponseDate && impsBetweenPicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Imps Between</div>
          <div className='global-options-dropdown'>{impsBetweenPicker}</div>
        </div>
      )}
    </div>
  );

  const modalBody = (
    <Modal.Body>
      {tabSelector}
      {selectedTab === 'Dates' && datesContainer}
      {selectedTab === 'Delivery' && deliveryContainer}
      {selectedTab === 'Filters' && filtersContainer}
      {selectedTab === 'KPI, etc.' && globalOptionsContainer}
      {selectedTab === 'Metrics Table' && metricsTableContainer}
      {selectedTab === 'Overview' && overviewContainer}
      {selectedTab === 'Preset Group' && groupsContainer}
    </Modal.Body>
  );

  const modalFooter = (
    <Modal.Footer>
      <Button
        size={Size.MD}
        className='cancel-button'
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        onClick={() => onHide()}
      >
        Cancel
      </Button>
      <Button
        size={Size.MD}
        className='preview-button'
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        onClick={() =>
          saveAndApplyOnClick({
            ...pagePresetChanges,
            temporary: true,
            id: (pagePresetChanges.temporary
              ? pagePresetChanges.id
              : undefined) as MetricsPagePreset['id']
          })
        }
        disabled={disableApply}
      >
        Temp Preview
      </Button>
      <Button
        size={Size.MD}
        className='save-and-apply-button'
        disabled={disableApply}
        onClick={() =>
          saveAndApplyOnClick({ ...pagePresetChanges, name: newName ?? pagePresetChanges.name })
        }
      >
        Save and Apply
      </Button>
    </Modal.Footer>
  );

  return (
    <Modal centered className='edit-page-preset-modal' onHide={() => onHide()} show size='lg'>
      {modalHeader}
      {modalBody}
      {modalFooter}
    </Modal>
  );
};
